<template>
  <Card cardClasses="shadow-sm border-0">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-clock-history me-2"></i>Shift Details
      </h4>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" @click="goToShifts">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading shift details...</p>
      </div>

      <div v-if="!loading" class="shift-details-content fade-in">
        <div class="text-center mb-4">
          <h1 class="shift-title">{{ shift.reference }}</h1>
          <div class="d-flex justify-content-center align-items-center mt-2">
            <Badge pill :type="shift.status === 'active' ? 'success' : 'secondary'" class="me-3">
              {{ shift.status }}
            </Badge>
          </div>
        </div>

        <ul class="nav nav-tabs nav-fill mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'details' }" @click="activeTab = 'details'" href="#">Details</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{ active: activeTab === 'summary' }" @click="activeTab = 'summary'" href="#">Summary</a>
          </li>
          <li class="nav-item" v-if="shift.z_report">
            <a class="nav-link" :class="{ active: activeTab === 'zreport' }" @click="activeTab = 'zreport'" href="#">Z-Report</a>
          </li>
        </ul>

        <div class="tab-content">
          <div v-if="activeTab === 'details'">
            <div class="card border-0 shadow-sm mb-4">
              <div class="card-header bg-light py-3">
                <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Shift Information</h5>
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Reference</h6>
                      <p class="info-value">{{ shift.reference }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Store</h6>
                      <p class="info-value">{{ shift.store?.name || 'N/A' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">User</h6>
                      <p class="info-value">{{ shift.user?.first_name }} {{ shift.user?.last_name }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Status</h6>
                      <Badge pill :type="shift.status === 'active' ? 'success' : 'secondary'">
                        {{ shift.status }}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card border-0 shadow-sm mb-4">
              <div class="card-header bg-light py-3">
                <h5 class="mb-0"><i class="bi bi-cash-stack me-2"></i>Cash Information</h5>
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Opening Cash</h6>
                      <p class="info-value text-success fw-bold">{{ formatNumber(shift.opening_cash) }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Closing Cash</h6>
                      <p class="info-value" :class="shift.closing_cash ? 'text-primary fw-bold' : ''">
                        {{ shift.closing_cash ? formatNumber(shift.closing_cash) : 'Not closed yet' }}
                      </p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Expected Cash</h6>
                      <p class="info-value">{{ shift.expected_cash ? formatNumber(shift.expected_cash) : '-' }}</p>
                    </div>
                  </div>
                  <div class="col-md-6 col-lg-3">
                    <div class="info-group">
                      <h6 class="info-label">Cash Variance</h6>
                      <p class="info-value" :class="getCashDifferenceClass(shift.cash_difference)">
                        {{ shift.cash_difference ? 'KES ' + shift.cash_difference : '-' }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card border-0 shadow-sm mb-4">
              <div class="card-header bg-light py-3">
                <h5 class="mb-0"><i class="bi bi-calendar-event me-2"></i>Time Information</h5>
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="info-group">
                      <h6 class="info-label">Started At</h6>
                      <p class="info-value">{{ formatDate(shift.started_at) }}</p>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="info-group">
                      <h6 class="info-label">Ended At</h6>
                      <p class="info-value">{{ shift.ended_at ? formatDate(shift.ended_at) : 'Still active' }}</p>
                    </div>
                  </div>
                  <div class="col-md-12" v-if="shift.notes">
                    <div class="info-group">
                      <h6 class="info-label">Notes</h6>
                      <p class="info-value">{{ shift.notes }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-center gap-2 mt-4">
              <button 
                v-if="shift.status === 'active'" 
                class="btn btn-warning" 
                @click="openEndShiftModal">
                <i class="bi bi-stop-circle me-1"></i> End Shift
              </button>
              <button 
                v-if="shift.status === 'closed' && !shift.z_report" 
                class="btn btn-success" 
                @click="openGenerateZReportModal">
                <i class="bi bi-file-earmark-text me-1"></i> Generate Z-Report
              </button>
              <button 
                v-if="shift.z_report" 
                class="btn btn-info" 
                @click="viewZReport(shift.z_report.id)">
                <i class="bi bi-eye me-1"></i> View Z-Report
              </button>
            </div>
          </div>

          <div v-if="activeTab === 'summary'">
            <div v-if="loadingSummary" class="text-center py-5">
              <Spinner spinner-class="mb-1" />
              <p class="text-muted mt-2">Loading shift summary...</p>
            </div>

            <div v-if="!loadingSummary && summary" class="row">
              <div class="col-md-6 col-lg-3 mb-4">
                <div class="card border-0 shadow-sm h-100">
                  <div class="card-body text-center">
                    <i class="bi bi-receipt fs-1 text-primary mb-2"></i>
                    <h3 class="mb-0">{{ summary.total_transactions || 0 }}</h3>
                    <p class="text-muted mb-0">Total Transactions</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-lg-3 mb-4">
                <div class="card border-0 shadow-sm h-100">
                  <div class="card-body text-center">
                    <i class="bi bi-cash-stack fs-1 text-success mb-2"></i>
                    <h3 class="mb-0">{{ formatNumber(summary.total_sales || 0) }}</h3>
                    <p class="text-muted mb-0">Total Sales</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-lg-3 mb-4">
                <div class="card border-0 shadow-sm h-100">
                  <div class="card-body text-center">
                    <i class="bi bi-cash fs-1 text-info mb-2"></i>
                    <h3 class="mb-0">{{ formatNumber(summary.total_cash || 0) }}</h3>
                    <p class="text-muted mb-0">Cash Payments</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-lg-3 mb-4">
                <div class="card border-0 shadow-sm h-100">
                  <div class="card-body text-center">
                    <i class="bi bi-phone fs-1 text-warning mb-2"></i>
                    <h3 class="mb-0">{{ formatNumber(summary.total_mpesa || 0) }}</h3>
                    <p class="text-muted mb-0">M-Pesa Payments</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-lg-3 mb-4">
                <div class="card border-0 shadow-sm h-100">
                  <div class="card-body text-center">
                    <i class="bi bi-credit-card fs-1 text-danger mb-2"></i>
                    <h3 class="mb-0">{{ formatNumber(summary.total_pdq || 0) }}</h3>
                    <p class="text-muted mb-0">PDQ Payments</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-lg-3 mb-4">
                <div class="card border-0 shadow-sm h-100">
                  <div class="card-body text-center">
                    <i class="bi bi-percent fs-1 text-secondary mb-2"></i>
                    <h3 class="mb-0">{{ formatNumber(summary.total_discount || 0) }}</h3>
                    <p class="text-muted mb-0">Total Discounts</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-lg-3 mb-4">
                <div class="card border-0 shadow-sm h-100">
                  <div class="card-body text-center">
                    <i class="bi bi-receipt-cutoff fs-1 text-primary mb-2"></i>
                    <h3 class="mb-0">{{ formatNumber(summary.total_tax || 0) }}</h3>
                    <p class="text-muted mb-0">Total Tax</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-lg-3 mb-4">
                <div class="card border-0 shadow-sm h-100">
                  <div class="card-body text-center">
                    <i class="bi bi-arrow-return-left fs-1 text-danger mb-2"></i>
                    <h3 class="mb-0">{{ summary.total_returns || 0 }}</h3>
                    <p class="text-muted mb-0">Returns ({{ formatNumber(summary.total_return_amount || 0) }})</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'zreport' && shift.z_report">
            <div class="alert alert-success">
              <i class="bi bi-check-circle me-2"></i>
              Z-Report has been generated for this shift.
              <button class="btn btn-sm btn-success ms-3" @click="viewZReport(shift.z_report.id)">
                <i class="bi bi-eye me-1"></i> View Full Report
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal :open="endShiftModal" @closed="endShiftModal = false">
      <div class="modal-header">
        <h5 class="modal-title m-0">
          <i class="bi bi-stop-circle me-2 text-warning"></i>End Shift
        </h5>
        <button @click="endShiftModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="endShift">
          <div class="row">
            <div class="form-group col-md-12 mb-3">
              <label for="closing_cash" class="form-label fw-medium mb-2">Closing Cash Amount:</label>
              <input type="number" step="0.01" class="form-control" v-model="endShiftData.closing_cash" required>
            </div>
            <div class="form-group col-md-12 mb-3">
              <label for="notes" class="form-label fw-medium mb-2">Notes (Optional):</label>
              <textarea class="form-control" v-model="endShiftData.notes" rows="3"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="endShiftModal = false">Cancel</button>
            <button type="submit" class="btn btn-warning">
              <i class="bi bi-stop-circle me-1"></i> End Shift
            </button>
          </div>
        </form>
      </div>
    </Modal>

    <Modal :open="generateZReportModal" @closed="generateZReportModal = false">
      <div class="modal-header">
        <h5 class="modal-title m-0">
          <i class="bi bi-file-earmark-text me-2 text-success"></i>Generate Z-Report
        </h5>
        <button @click="generateZReportModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="generateZReport">
          <div class="row">
            <div class="form-group col-md-12 mb-3">
              <label for="notes" class="form-label fw-medium mb-2">Notes (Optional):</label>
              <textarea class="form-control" v-model="zReportData.notes" rows="3" placeholder="End of day reconciliation"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="generateZReportModal = false">Cancel</button>
            <button type="submit" class="btn btn-success">
              <i class="bi bi-file-earmark-text me-1"></i> Generate Report
            </button>
          </div>
        </form>
      </div>
    </Modal>
  </Card>
</template>

<script>
import shiftService from "@/api/shiftService"
import zreportService from "@/api/zreportService"
import router from "@/router"
import formatDate from "@/utils/formatDate"
import formatNumber from "@/utils/formatNumber"

export default {
  name: "ShiftDetails",

  data(){
    return {
      loading: true,
      loadingSummary: false,
      shift: {},
      summary: null,
      activeTab: 'details',
      endShiftModal: false,
      generateZReportModal: false,
      endShiftData: {
        closing_cash: 0,
        notes: ''
      },
      zReportData: {
        notes: ''
      },
    }
  },

  methods:{
    formatDate,
    formatNumber,

    async fetchShift() {
      this.loading = true
      const id = this.$route.params.id
      this.shift = await shiftService.getShift(id)
      this.loading = false
    },

    async fetchSummary() {
      this.loadingSummary = true
      const id = this.$route.params.id
      this.summary = await shiftService.getShiftSummary(id)
      this.loadingSummary = false
    },

    goToShifts() {
      router.push('/shifts')
    },

    openEndShiftModal() {
      this.endShiftData = {
        closing_cash: 0,
        notes: ''
      }
      this.endShiftModal = true
    },

    async endShift() {
      const result = await shiftService.endShift(this.shift.id, this.endShiftData)
      if (result) {
        this.endShiftModal = false
        await this.fetchShift()
        await this.fetchSummary()
      }
    },

    openGenerateZReportModal() {
      this.zReportData = {
        notes: ''
      }
      this.generateZReportModal = true
    },

    async generateZReport() {
      const result = await zreportService.generateZReport(this.shift.id, this.zReportData)
      if (result) {
        this.generateZReportModal = false
        await this.fetchShift()
        router.push('/z-report/' + result.id)
      }
    },

    viewZReport(id) {
      router.push('/z-report/' + id)
    },

    getCashDifferenceClass(difference) {
      if (!difference) return 'text-muted'
      const diff = parseFloat(difference)
      if (diff > 0) return 'text-success fw-bold'
      if (diff < 0) return 'text-danger fw-bold'
      return 'text-muted'
    },
  },

  watch: {
    activeTab(newTab) {
      if (newTab === 'summary' && !this.summary) {
        this.fetchSummary()
      }
    }
  },

  async mounted() {
    await this.fetchShift()
  }
}
</script>

<style scoped>
.fade-in {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.shift-title {
  font-size: 2rem;
  font-weight: 600;
  color: #0d4072;
}

.info-group {
  padding: 10px;
}

.info-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.info-value {
  font-size: 1rem;
  color: #212529;
  margin-bottom: 0;
}
</style>
